/**
 * Shared rules for the "log an action" flow (#185 frame counts, #170
 * follow-ups). Kept out of the components because mobile needs exactly the
 * same rules, and the two screens have drifted before.
 *
 * The mobile copy lives at mobile/src/services/hiveActions.js and must stay
 * identical. There is no shared package between the two apps, so this is a
 * deliberate duplicate rather than an import.
 */

/** Action types where "how many frames?" is a meaningful question.
 *
 * Matches the CK_HiveActions_type constraint, as V105 left it. The
 * quick-action lists currently surface only PulledFrames and
 * ReplacedFrames, but the schema also allows AddedFrames and
 * RotatedFrames, and all four want a count -- so the rule keys off the
 * action type, not off what a particular menu happens to show today.
 *
 * FOUR, NOT SIX. FramePull and FrameReplace used to be listed here too,
 * as "legacy spellings still permitted by the CHECK constraint. Harmless
 * to include." They were not harmless: duplicate detection keys on
 * action_type (#375), so a second name for one job made the check blind
 * to any repeat logged under the other -- and the frame actions are
 * precisely where that was reported. V105 moved the rows and narrowed the
 * constraint; nothing can write them now.
 */
export const FRAME_ACTIONS = new Set([
  "PulledFrames",
  "ReplacedFrames",
  "AddedFrames",
  "RotatedFrames",
]);

export function needsFrameCount(actionType) {
  return FRAME_ACTIONS.has(actionType);
}

/** Relative options rather than a calendar.
 *
 * Beekeepers schedule revisits as "come back in a week", not by picking a
 * date off a grid, and this gets used outdoors wearing gloves. Presets also
 * avoid pulling in a native date-picker dependency on mobile, which has none
 * today.
 */
export const FOLLOW_UP_PRESETS = [
  { key: "3d", label: "3 days", days: 3 },
  { key: "1w", label: "1 week", days: 7 },
  { key: "2w", label: "2 weeks", days: 14 },
  { key: "1m", label: "1 month", days: 30 },
];

/** Local-date ISO string (YYYY-MM-DD) `days` from now.
 *
 * Built from local Y/M/D parts rather than toISOString(), which converts to
 * UTC first and therefore lands on the previous day for anyone west of
 * Greenwich for most of their evening. due_date is a DATE column: the
 * intended day is the user's day, not a UTC instant.
 */
export function dueDateInDays(days, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Build the details payload for an action, or undefined when there is
 *  nothing to record. Undefined (not {}) so the API stores NULL rather than
 *  an empty JSON object. */
export function buildActionDetails({ note, frameCount } = {}) {
  const details = {};
  const trimmed = typeof note === "string" ? note.trim() : "";
  if (trimmed) details.note = trimmed;
  if (Number.isFinite(frameCount) && frameCount > 0) details.frame_count = frameCount;
  return Object.keys(details).length ? details : undefined;
}

/** Human summary of an action's details for the timeline.
 *
 * Extends the note-only rendering added in #178 rather than adding a second
 * parser. Tolerates details arriving as a JSON string (from SQL) or an
 * object (from the offline queue), and returns null for anything it cannot
 * read -- one malformed row must not blank the timeline.
 */
export function describeActionDetails(details) {
  if (!details) return null;
  let parsed = details;
  if (typeof details === "string") {
    try {
      parsed = JSON.parse(details);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;

  const parts = [];
  const count = parsed.frame_count;
  if (Number.isFinite(count) && count > 0) {
    parts.push(`${count} frame${count === 1 ? "" : "s"}`);
  }
  const note = parsed.note ?? parsed.notes;
  if (typeof note === "string" && note.trim()) parts.push(note.trim());

  return parts.length ? parts.join(" — ") : null;
}

/** "North Orchard — Hive 3", never a bare number.
 *
 * hive_number restarts at 1 per site, so "Hive 3" identifies nothing once
 * there are two sites (#173). Matters most in pickers (#211): the value
 * behind the option is hive_id, which the user never sees and must never
 * be asked to know.
 *
 * Mirrors mobile's services/dashboardPriority.js hiveLabel so the two
 * clients cannot drift on how a hive is named.
 */
export function hiveLabel({ site_name, hive_number } = {}) {
  if (hive_number == null) return site_name || "Hive";
  return site_name ? `${site_name} — Hive ${hive_number}` : `Hive ${hive_number}`;
}

/** How long ago something happened, in whole days: "Today", "Yesterday",
 *  "18 days ago".
 *
 * Kept in days rather than rolling up to weeks or months. Inspection
 * intervals are set in days and beekeepers talk in days -- "6 weeks ago"
 * reads friendlier but throws away the number the user is actually
 * comparing against their interval.
 *
 * Compared at local midnight, not by dividing elapsed milliseconds: a check
 * logged at 11pm yesterday is "Yesterday", not "0 days ago", and one logged
 * at 1am today is "Today" even though barely any time has passed.
 */
export function daysAgo(value, now = new Date()) {
  if (!value) return null;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((midnight(now) - midnight(then)) / 86_400_000);
  // A future timestamp is a clock-skew or bad-data case, not something to
  // render as "-2 days ago".
  if (days < 0) return formatDateOnly(value);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

/** Format a value that may be a calendar day OR a full timestamp.
 *
 * The two need opposite handling, and getting it wrong shifts the displayed
 * day by one:
 *
 *   new Date("2026-09-01")            -> parsed as UTC midnight, so
 *                                        toLocaleDateString() shows 31 Aug
 *                                        anywhere west of Greenwich.
 *   new Date("2026-09-01T00:30:00Z")  -> a real instant; must be converted
 *                                        to the viewer's zone, which is the
 *                                        whole point.
 *
 * So a date-only string is pinned to local midnight and a timestamp is left
 * alone. The API distinguishes them deliberately: DATE columns serialize as
 * "YYYY-MM-DD" and DATETIME2 columns as ISO-8601 with a "Z" (see
 * api/security/_json_default).
 */
export function formatDateOnly(value, locale = undefined) {
  if (!value) return null;
  const text = String(value);
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(text.slice(0, 10)) && !text.includes("T");
  const d = dateOnly ? new Date(`${text.slice(0, 10)}T00:00:00`) : new Date(text);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale);
}

/**
 * Has this already been logged on this hive today? (#375)
 *
 * Reported from a device: six "Replaced Frames" and three "Pulled Frames"
 * on one hive, with nothing asked. That is one person logging the same
 * job repeatedly -- not two people who each did the work -- and the
 * evidence is sitting in the timeline the screen has already loaded.
 *
 * WHY THIS IS A CLIENT CHECK AND THE DASHBOARD IS STILL NEEDED. They
 * answer different halves of the same question:
 *
 *   * ONE PERSON, SAME DAY. You logged it, were unsure it saved, and
 *     logged it again. The other entry is already on this device, so the
 *     cheapest possible moment to ask is before the second write -- and
 *     it works with no signal, which is exactly when "did that save?"
 *     gets asked.
 *
 *   * TWO PEOPLE. Neither device has the other's entry until both sync,
 *     so no client can see it. That is what services/duplicates detects
 *     after a push, and why its docstring says a write-time warning
 *     cannot work THERE.
 *
 * Neither replaces the other, and doing only the second would mean the
 * commonest case is caught hours later on a dashboard instead of in the
 * two seconds where the answer is obvious.
 *
 * SAME DAY, not a window. SAME_USER_WINDOW_DAYS is 1 for the same reason:
 * forgetting you already did something is same-day. Anything wider starts
 * calling deliberate repeat work a mistake, and a question people answer
 * "yes, I meant it" twice is a question they stop reading.
 *
 * Superseded rows are ignored: one already folded away is an answer
 * somebody has given, and asking again would reopen it.
 */
export function alreadyLoggedToday(actions, actionType, today = localDay()) {
  return (actions || []).some(
    (a) => a.action_type === actionType
      && !a.superseded_by_action_id
      && String(a.action_date || "").slice(0, 10) === today,
  );
}

/** Today, as the user's own day.
 *
 * Built from local parts rather than toISOString(), which converts to UTC
 * first and therefore lands on the previous day for anyone west of
 * Greenwich for most of their evening -- the same trap dueDateInDays
 * documents. An evening inspection must not be compared against
 * tomorrow.
 */
export function localDay(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/* ── WITHDRAWAL PERIODS (#404) ────────────────────────────────────────
 *
 * Asked from a device while testing #398's withdrawal-period field:
 *
 *     "should we add a warning or alert if you're within a withdrawal
 *      period for the 'Pull Frames' and 'Harvest' forms to inform the
 *      user they shouldn't pull honey until 'X' date due to treatment
 *      withdrawal?"
 *
 * A withdrawal period is the interval after treating during which honey
 * must not enter the food chain. Everything needed was already recorded
 * and nothing read it for this purpose.
 *
 * THE SENTENCE LIVES HERE, not in the two UIs, for the reason terms.js
 * gives about "withdrawal period" itself: two apps explaining one
 * food-safety fact in two ways is worse than one explaining it once.
 */

/** Actions that take honey out of a hive.
 *
 * NOT every frame action. ReplacedFrames, AddedFrames and RotatedFrames
 * put frames in or move them about -- warning on those would fire on
 * hive management that has nothing to do with selling honey, and a
 * warning that fires when it need not is one people learn to dismiss.
 *
 * PulledFrames is the borderline one and it is IN, because pulling is
 * how honey leaves. It is also why this informs rather than refuses:
 * frames come out to inspect, to rebalance a strong colony, to move
 * brood, and a block would be wrong for all three.
 */
export const HONEY_ACTIONS = new Set(["PulledFrames", "Harvest"]);

export function takesHoney(actionType) {
  return HONEY_ACTIONS.has(actionType);
}

/* Fixed rather than locale-formatted, so both apps print one date the
   same way. toLocaleDateString differs between platforms and devices,
   and a compliance date that reads differently on the phone and the desk
   is the drift this module exists to prevent. */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-17" -> "17 Sep 2026". Parsed as PARTS, never as a Date.
 *
 * `new Date("2026-09-17")` is parsed as UTC midnight and then rendered
 * locally, which prints the 16th for anyone west of Greenwich -- the
 * same off-by-one localDay() exists to avoid, and the last place it may
 * appear is a date somebody checks before selling honey.
 */
export function formatDay(iso) {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/** The binding withdrawal for one hive, or null if its honey is clear.
 *
 * COMPARED LOCALLY, which is the whole reason the server does not decide
 * this. safe_from is a DATE and "has it passed" belongs to the user's
 * calendar: west of Greenwich the server rolls over first and would
 * clear a hive while it is still yesterday for the keeper in front of
 * it. The server sends a day of slack for the same reason from the other
 * side, and this is what filters it out.
 *
 * Strings compared as strings: ISO dates sort correctly, and building
 * Date objects to compare them reintroduces the timezone the format was
 * chosen to avoid.
 */
export function withdrawalFor(withdrawal, hiveId, today = localDay()) {
  const row = (withdrawal || []).find(
    (w) => String(w.hive_id) === String(hiveId));
  if (!row) return null;

  const safeFrom = String(row.safe_from || "").slice(0, 10);
  // Safe FROM that day, so the day itself is clear.
  if (!safeFrom || safeFrom <= today) return null;

  return { ...row, safe_from: safeFrom };
}

/** What to tell somebody about to take honey from a treated hive.
 *
 * NAMES THE DATE rather than saying "you are inside a withdrawal
 * period". The abstract version sends somebody to look it up; a date is
 * actionable at a glance, which is the whole point when this is read in
 * a yard.
 *
 * Returns null when there is nothing to say -- no treatment, cleared
 * already, or an action that does not take honey.
 */
export function withdrawalWarning(withdrawal, hiveId, actionType, today = localDay()) {
  if (!takesHoney(actionType)) return null;

  const active = withdrawalFor(withdrawal, hiveId, today);
  if (!active) return null;

  return `${active.treatment_name} was applied on ${formatDay(active.application_date)}. `
    + `Honey from this hive should not be sold until ${formatDay(active.safe_from)}.`;
}

/** "2026-10-02" -> "2 Oct", or "2 Oct 2027" if that is not this year.
 *
 * THE YEAR IS THE PART THAT CAN GO. Reported from a device: the badge
 * "Withdrawal to 2 Oct 2026" ran off the edge of a hive row, and worse
 * beside "Closed until 9/18/2026" on a hive that is also shut for the
 * season.
 *
 * A withdrawal period is days or weeks, so the year is almost always the
 * current one and adds nothing but width. It comes back the moment the
 * date lands in a different year, which is exactly when a bare "2 Jan"
 * would be ambiguous.
 *
 * The FULL date is unchanged in the sentence on the form -- that one is
 * read standing still, has room, and is the compliance statement.
 */
export function formatDayShort(iso, today = localDay()) {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-");
  if (!y || !m || !d) return "";
  const thisYear = String(today).slice(0, 4);
  return y === thisYear
    ? `${Number(d)} ${MONTHS[Number(m) - 1]}`
    : `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/** A short marker for a hive list row (#404 follow-up).
 *
 * Asked for after the form warnings landed:
 *
 *     "yes I think the badge is a VERY good idea I didn't think of but I
 *      still think a warning on the Pull Frames, and other tasks that
 *      should be avoided during the Withdrawal period for any keeper that
 *      is working the same site and using the 'Next Hive' option or maybe
 *      just doesn't pay enough attention to the badges."
 *
 * BOTH, AND THE REASONING IS THE POINT. The badge puts the fact where
 * somebody sees it before they walk out. The form warning catches the
 * person already standing at the hive who arrived by tapping "Next Hive"
 * and never looked at a list at all -- and the person who did look and
 * did not register it. A badge alone would fail exactly the keeper
 * working a site hive by hive, which is the normal way to work a site.
 *
 * SHORTER THAN THE SENTENCE, because a table cell is not a place for one.
 * The date is kept because the date is the actionable part; the treatment
 * name is dropped, because the row has no room and the form warning names
 * it at the moment it matters.
 *
 * NO ACTION TYPE. A badge is not about what you are doing, it is about
 * what is true of the hive.
 */
export function withdrawalBadge(withdrawal, hiveId, today = localDay()) {
  const active = withdrawalFor(withdrawal, hiveId, today);
  return active ? `Withdrawal to ${formatDayShort(active.safe_from, today)}` : null;
}

/** "Wait 14 days before taking honey", for a treatment picker (#404 follow-up).
 *
 * Asked for after the withdrawal warnings shipped:
 *
 *     "there's nothing that tells the user at the time how long the
 *      withdrawal period is....New Keepers will need to know when they
 *      apply those treatments how long to wait"
 *
 * Right, and it is the moment the number actually changes a decision. The
 * warning on the harvest form tells you afterwards; this tells you while
 * you are choosing what to put in the hive, which is when somebody might
 * pick a different product.
 *
 * ZERO IS SAID OUT LOUD rather than left blank. A treatment with no wait
 * is a real and common answer -- sugar dusting, drone removal -- and a
 * silent row would read as "we do not know", which is the one thing it
 * must not mean on a food-safety number.
 *
 * Shared, like every other sentence about this term, so the phone and the
 * desk cannot explain one compliance fact two ways.
 */
export function withdrawalNote(treatment) {
  const days = Number(treatment?.withdrawal_period_days ?? 0);
  if (!Number.isFinite(days) || days <= 0) return "No wait before harvesting";
  return `Wait ${days} ${days === 1 ? "day" : "days"} before taking honey`;
}

/** The badge, split for a narrow column: ["Withdrawal", "Until 25 Sep"].
 *
 * Reported from the web app: "probably need to be split so it says
 * 'Withdrawal' and then 'Until <Date>' below it so it looks cleaner".
 *
 * Returns the PARTS rather than a string with a newline in it, because
 * each platform lays them out differently -- a table cell stacks them,
 * a pill on a phone does not -- and a `
` in a label is a rendering
 * decision smuggled into the wording. Null when there is nothing to say,
 * exactly like withdrawalBadge.
 */
export function withdrawalBadgeParts(withdrawal, hiveId, today = localDay()) {
  const active = withdrawalFor(withdrawal, hiveId, today);
  if (!active) return null;
  return { lead: "Withdrawal", until: `Until ${formatDayShort(active.safe_from, today)}` };
}

/** The quick actions, grouped so the screen does the triage (#411 follow-up).
 *
 * Asked for from a device once the fourth and fifth actions arrived:
 *
 *     "I'm open to your recommendations on how to resolve the growing
 *      number of quick actions."
 *
 * ── WHY GROUPS AND NOT THE OVERFLOW MENU ──
 *
 * #411 put Add and Rotate Frames behind a "More" menu, which is
 * docs/UI_ACTIONS.md rule 6 applied literally: four on a surface, then a
 * menu. It was reported back as inconsistent, and on reflection it is --
 * two frame actions on the row and two behind a button, with nothing
 * about the pair explaining which.
 *
 * Rule 6's REASON is "a user scanning nineteen buttons is doing the
 * triage the screen should have done". Groups do that triage; a menu
 * only hides half of it, and hides it by recency of addition rather than
 * by meaning. Seven labelled actions in three named groups scan faster
 * than five plus a mystery.
 *
 * It also keeps every action ONE TAP. A dropdown would have cost the
 * common case -- Health Check is the most frequent thing on this screen
 * -- a tap to open and a tap to choose, every time.
 *
 * ── THE GROUPING ──
 *
 * "Season" is its own group rather than sitting with the health actions,
 * which is where the original suggestion put it. Closing a hive for
 * winter is not a judgement about the colony: it changes the inspection
 * cadence for months (#389), and filing it beside Health Check invites
 * it being read as one.
 *
 * Shared so the phone and the desk cannot end up with two different
 * ideas of what belongs together.
 */
export const ACTION_GROUPS = [
  {
    key: "hive",
    label: "At the hive",
    actions: [
      { type: "HealthCheck", label: "Health Check" },
    ],
  },
  {
    key: "box",
    label: "In the box",
    actions: [
      { type: "PulledFrames", label: "Pull Frames" },
      { type: "ReplacedFrames", label: "Replace Frames" },
      { type: "AddedFrames", label: "Add Frames" },
      { type: "RotatedFrames", label: "Rotate Frames" },
    ],
  },
  {
    key: "season",
    label: "Season",
    actions: [
      { type: "CloseSeason", label: "Close Season" },
    ],
  },
];
